
import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useMemorial } from "@/contexts/MemorialContext";
import { formatDateForDisplay } from "@/utils/dateUtils";
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";

interface ProfileSectionProps {
  showProfileImage: boolean;
  setShowProfileImage: (show: boolean) => void;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  showProfileImage,
  setShowProfileImage
}) => {
  const { personalDetails } = useMemorial();

  const getDateDisplay = (birthDate?: string, deathDate?: string) => {
    const birth = birthDate ? formatDateForDisplay(birthDate) : '';
    const death = deathDate ? formatDateForDisplay(deathDate) : '';

    if (!birth && !death) return '';
    if (!death) return birth;
    if (!birth) return `d. ${death}`;
    return `${birth} - ${death}`;
  };

  const displayName = buildMemorialDisplayName({
    first_name: personalDetails.firstName,
    last_name: personalDetails.lastName,
    person2_first_name: personalDetails.person2FirstName,
    person2_last_name: personalDetails.person2LastName,
  });

  const person1Dates = getDateDisplay(personalDetails.birthDate, personalDetails.deathDate);
  const person2Dates = personalDetails.isDoubleMemorial
    ? getDateDisplay(personalDetails.person2BirthDate, personalDetails.person2DeathDate)
    : '';

  // For double memorials, label each date line with the person's name so it's
  // clear which dates belong to whom. Single memorials need no label — the one
  // name already sits directly above the dates.
  const withName = (name: string | undefined, dates: string) => {
    const trimmedName = name?.trim();
    if (personalDetails.isDoubleMemorial && trimmedName && dates) {
      return `${trimmedName} · ${dates}`;
    }
    return dates;
  };

  const person1DateLine = withName(personalDetails.firstName, person1Dates);
  const person2DateLine = withName(personalDetails.person2FirstName, person2Dates);

  return (
    <>
      <div className="flex flex-col items-center pt-6 relative z-20">
        <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-white shadow-lg mb-4">
          <button
            onClick={() => setShowProfileImage(true)}
            className="w-full h-full bg-white hover:opacity-90 transition-opacity cursor-pointer"
          >
            <img
              src={personalDetails.profileImage || "/placeholder.svg"}
              alt="Portrait"
              className="w-full h-full object-cover"
            />
          </button>
        </div>
        <h1 className="font-heading text-2xl md:text-4xl mb-2 md:mb-3 text-center">
          {displayName}
        </h1>
        {person1Dates && (
          <p className="text-black text-base md:text-lg mb-0 text-center">
            {person1DateLine}
          </p>
        )}
        {person2Dates && (
          <p className="text-black text-base md:text-lg mb-0 text-center">
            {person2DateLine}
          </p>
        )}
        {!person1Dates && !person2Dates && (
          <p className="text-black text-base md:text-lg mb-0 text-center">&nbsp;</p>
        )}
      </div>

      <Dialog open={showProfileImage} onOpenChange={setShowProfileImage}>
        <DialogContent className="max-w-3xl p-0">
          <div className="aspect-square w-full">
            <img
              src={personalDetails.profileImage || "/placeholder.svg"}
              alt="Portrait"
              className="w-full h-full object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

